/*
  Smooth open/close for the accordion rows.

  A bare <details> snaps open, so the summary click is intercepted and the
  content wrapper's height is animated instead. `open` is set before the opening
  animation and only cleared after the closing one, so the content stays in the
  accessibility tree for as long as it is visible.
*/
(() => {
  const DURATION = 280;
  /* The previous curve (0.32, 0.72, 0, 1) front-loads ~85% of the motion into the
     first 40% of the duration, leaving a long, barely-visible tail that reads as a
     stall rather than a finish. This standard "ease" curve spreads the motion more
     evenly across the full duration. */
  const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class SmoothDetails {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector('.accordion__summary');
      this.wrapper = details.querySelector('.accordion__content-wrap');
      this.animation = null;

      if (!this.summary || !this.wrapper) return;

      this.summary.addEventListener('click', (event) => this.onClick(event));
      details.dataset.smooth = 'true';
    }

    onClick(event) {
      if (prefersReducedMotion()) return;

      event.preventDefault();

      // While closing, `open` is still set — treat a second click as "open again"
      // so the row reverses out of the running animation instead of stalling.
      if (this.details.open && !this.details.dataset.closing) {
        this.close();
      } else {
        this.open();
      }
    }

    currentHeight() {
      return this.wrapper.getBoundingClientRect().height;
    }

    animateTo(from, to, onFinish) {
      if (this.animation) this.animation.cancel();

      this.wrapper.style.overflow = 'hidden';
      this.animation = this.wrapper.animate(
        { height: [`${from}px`, `${to}px`] },
        { duration: DURATION, easing: EASING }
      );

      this.animation.onfinish = () => {
        this.animation = null;
        this.wrapper.style.overflow = '';
        this.wrapper.style.height = '';
        if (onFinish) onFinish();
      };
    }

    open() {
      // Measure before cancelling: a running close animation overrides the inline
      // height, and that in-flight value is exactly where the row should start.
      const from = this.currentHeight();
      if (this.animation) this.animation.cancel();
      this.wrapper.style.height = `${from}px`;
      delete this.details.dataset.closing;
      this.details.open = true;
      this.wrapper.style.height = 'auto';
      const to = this.currentHeight();
      this.wrapper.style.height = `${from}px`;
      this.animateTo(from, to);
    }

    close() {
      const from = this.currentHeight();
      this.details.dataset.closing = 'true';
      this.animateTo(from, 0, () => {
        this.details.open = false;
        delete this.details.dataset.closing;
      });
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('.accordion__item:not([data-smooth])').forEach((details) => new SmoothDetails(details));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor re-renders the section without a full page load
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
